// valuation_fcff.js
// FCFF Valuation (Option B: Driver-based FCFF)
// FIXED (read-only): EBIT margin, tax rate, terminal growth, WACC, WACC_Stable (backend-sent).
// VARIABLE (UI): Sales growth %, Dep % of Sales, Capex % of Sales, ΔWC % of Sales, forecast years.
//
// Core: FCFF = NOPAT + Dep - Capex - ΔWC
// NOPAT = EBIT * (1 - Tax), EBIT = Sales * EBITMargin

export const pctToDec = (x) => x / 100.0;
const round2 = (x) => Math.round(x * 100) / 100;
const clampMin = (x, min) => (x < min ? min : x);

function toSeries(valueOrArray, years, fallbackValue = 0) {
  if (Array.isArray(valueOrArray)) {
    const arr = valueOrArray.slice(0, years).map((v) => (v ?? fallbackValue));
    while (arr.length < years) arr.push(arr[arr.length - 1] ?? fallbackValue);
    return arr;
  }
  const v = valueOrArray ?? fallbackValue;
  return Array.from({ length: years }, () => v);
}

export function projectSalesSeries(baseSales, salesGrowthDecSeries) {
  const sales = [];
  let prev = baseSales;
  for (let i = 0; i < salesGrowthDecSeries.length; i += 1) {
    const g = salesGrowthDecSeries[i];
    const curr = round2(prev * (1 + g));
    sales.push(curr);
    prev = curr;
  }
  return sales; // [Sales_1..Sales_N]
}

export function pvSeries(rateDec, cashflows) {
  return cashflows.map((cf, i) => round2(cf / Math.pow(1 + rateDec, i + 1)));
}

export function terminalValueFromFcff(fcffN, gTermDec, waccStableDec) {
  const denom = clampMin(waccStableDec - gTermDec, 1e-6);
  const fcffNext = fcffN * (1 + gTermDec);
  return round2(fcffNext / denom);
}

export const pvTV = (waccDec, tv, years) => round2(tv / Math.pow(1 + waccDec, years));

/**
 * params expected:
 * {
 *   baseSales, forecastYears,
 *   drivers: { salesGrowthPct, depPctSales, capexPctSales, deltaWcPctSales },
 *   fixed: { ebitMarginPct, taxRatePct, waccPct, waccStablePct, terminalGrowthPct },
 *   bridge: { borrowings, cash, nonOpInvestments, sharesOutstanding, currentPrice }
 * }
 */
export default function computeFCFFValuation({
  stockName,
  params,
  salesGrowthDeltaPct = 0,
  depPctSalesDeltaPct = 0,
  capexPctSalesDeltaPct = 0,
  deltaWcPctSalesDeltaPct = 0,
  forecastYearsOverride = null,
}) {
  const { baseSales, forecastYears = 5, drivers, fixed, bridge } = params;
  const years = Math.max(1, Math.round(forecastYearsOverride ?? forecastYears));

  // -------- fixed (NOT user-editable) --------
  const ebitMarginDecSeries = toSeries(fixed.ebitMarginPct, years, 0).map(pctToDec);
  const taxDecSeries = toSeries(fixed.taxRatePct, years, 0.25).map(pctToDec);
  const waccDec = pctToDec(fixed.waccPct);
  const waccStableDec = pctToDec(fixed.waccStablePct);
  const gTermDec = pctToDec(fixed.terminalGrowthPct);

  // -------- variable (UI-editable) --------
  const salesGrowthPctSeries = toSeries(drivers.salesGrowthPct, years, 0)
    .map((v) => v + salesGrowthDeltaPct);
  const salesGrowthDecSeries = salesGrowthPctSeries.map(pctToDec);

  const depPctSeries = toSeries(drivers.depPctSales, years, 0)
    .map((v) => v + depPctSalesDeltaPct);
  const depDecSeries = depPctSeries.map(pctToDec);

  const capexPctSeries = toSeries(drivers.capexPctSales, years, 0)
    .map((v) => v + capexPctSalesDeltaPct);
  const capexDecSeries = capexPctSeries.map(pctToDec);

  const dWcPctSeries = toSeries(drivers.deltaWcPctSales, years, 0)
    .map((v) => v + deltaWcPctSalesDeltaPct);
  const dWcDecSeries = dWcPctSeries.map(pctToDec);

  // -------- projections --------
  const sales = projectSalesSeries(baseSales, salesGrowthDecSeries);

  const ebit = sales.map((s, i) => round2(s * ebitMarginDecSeries[i]));
  const nopat = ebit.map((x, i) => round2(x * (1 - (taxDecSeries[i] ?? 0.25))));

  const dep = sales.map((s, i) => round2(s * depDecSeries[i]));
  const capex = sales.map((s, i) => round2(s * capexDecSeries[i]));
  const deltaWc = sales.map((s, i) => round2(s * dWcDecSeries[i]));

  const fcff = nopat.map((n, i) => round2(n + dep[i] - capex[i] - deltaWc[i]));

  // -------- PVs / TV --------
  const pvFcff = pvSeries(waccDec, fcff);
  const pvFcffSum = round2(pvFcff.reduce((a, b) => a + b, 0));

  const tv = terminalValueFromFcff(fcff[years - 1], gTermDec, waccStableDec);
  const pvTv = pvTV(waccDec, tv, years);

  const ev = round2(pvFcffSum + pvTv);

  // Equity = EV - (Borrowings - Cash) + NonOpInvestments
  const netDebt = round2((bridge.borrowings ?? 0) - (bridge.cash ?? 0));
  const equityValue = round2(ev - netDebt + (bridge.nonOpInvestments ?? 0));

  const shares = clampMin(bridge.sharesOutstanding ?? 1, 1e-9);
  const valPerShare = round2(equityValue / shares);

  const currentPrice = params.currentPrice ?? bridge.currentPrice ?? 0;
  const marginOfSafety = round2((valPerShare - currentPrice) / clampMin(currentPrice, 1e-9));

  return {
    stockName,
    horizonYears: years,

    // fixed rates used
    wacc: waccDec,
    waccStable: waccStableDec,
    terminalGrowth: gTermDec,

    // series for UI charts
    sales,
    ebit,
    nopat,
    dep,
    capex,
    deltaWc,
    fcff,
    pvFcff,

    // valuation blocks
    pvFcffSum,
    tv,
    pvTv,
    ev,
    netDebt,
    equityValue,
    valPerShare,
    perShare: valPerShare,
    marginOfSafety,

    // echo for debugging / explainability
    params: {
      currentPrice,
      ...params
    },
    paramsUsed: {
      variable: {
        salesGrowthPctSeries,
        depPctSeries,
        capexPctSeries,
        deltaWcPctSeries: dWcPctSeries,
      },
      fixed,
      bridge,
    },
  };
}
